import { Box } from "@mui/material";
import type { FC, ReactNode } from "react";
import { LOCATION_GRID } from "@/theme/tokens";

type Props = {
	/** The page's column: header, toolbar, strip and grid. */
	children: ReactNode;
	/** What sits outside the column (dialogs, the New location popup). */
	outside?: ReactNode;
};

/**
 * The Locations page's frame, shared by the page and its loading skeleton so the skeleton's blocks sit exactly where
 * the page's land. The page scrolls itself because the shared ContentWrapper clips vertical overflow; the scroller is
 * the full-width outer box so its scrollbar sits at the window edge, not at the capped column's edge.
 */
export const LocationsPageFrame: FC<Props> = ({ children, outside }) => (
	<Box sx={{ height: "100%", width: "100%", overflowY: "auto", display: "flex", flexDirection: "column" }}>
		{/* A full-height column so the grid can flex into whatever the header leaves. `minHeight: 0` here
		    and on the grid wrapper stops `min-height: auto` from growing the column to the grid's full
		    content height — that would scroll the page instead of the grid and render every row. */}
		<Box
			// The grid is the page, so the column is the grid's (`LOCATION_GRID.PAGE_MAX_WIDTH`), not a page of cards'.
			sx={{
				// A shorter top than the sides: every pixel above the toolbar is one the grid doesn't get.
				px: { xs: 2, sm: 3 },
				pt: 2,
				pb: { xs: 2, sm: 3 },
				maxWidth: LOCATION_GRID.PAGE_MAX_WIDTH,
				mx: "auto",
				width: "100%",
				flex: 1,
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
			}}
		>
			{children}
		</Box>
		{outside}
	</Box>
);
